import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { DEFAULT_ORDER_STATUS } from '../../../../helpers/constants';
import { computeCollectedFees, sumInvoiceLineAmounts } from '../../../../helpers/collectedFees';
import { canViewCollectedFees, isClient, isStaff } from '../../../../helpers/global';
import { onScrollToErrorPosition } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import useCountyDefaultProcessors from '../../../../hooks/useCountyDefaultProcessors';
import AssignedProcessorsMultiSelectModal from './Common/AssignedProcessorsMultiSelect';

const InitialFormFieldError = { isAssignedProcessorsNotValid: false, isStatusNotValid: false };

const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA', 'Information Received'];

const LienOrderForm = ({ onSubmit, isEditingOrder, loading, isPublic, county }) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const [showAssignedProcessors, setShowAssignedProcesser] = useState(null);
  const [formFieldError, setFormFieldError] = useState(!isClient(role) ? InitialFormFieldError : {});
  const lienSearchStatuses = useAppChoices('Lien Search Status');
  const canPreselectProcessors = isStaff(role) && !isPublic && !isEditingOrder?._id;
  const { processors: countyDefaultProcessors, loading: loadingCountyDefaultProcessors } = useCountyDefaultProcessors(
    county,
    canPreselectProcessors
  );
  const preselectedCounty = useRef(null);
  const [formData, setFormData] = useState(
    !isClient(role) && !isPublic
      ? {
          status: DEFAULT_ORDER_STATUS,
          assignedProcessors: [],
          estimatedTurnAroundTime: '',
          hardCost: '',
          neededDate: '',
          rush: false,
          processingFee: isEditingOrder?.processingFee
        }
      : { rush: false, neededDate: '', processingFee: isEditingOrder?.processingFee }
  );

  useEffect(() => {
    if (isEditingOrder?._id) {
      setFormData({
        status: isEditingOrder?.status || DEFAULT_ORDER_STATUS,
        assignedProcessors: isEditingOrder?.assignedProcessors || [],
        estimatedTurnAroundTime: isEditingOrder?.estimatedTurnAroundTime
          ? moment(isEditingOrder?.estimatedTurnAroundTime).format('YYYY-MM-DD')
          : '',
        hardCost: isEditingOrder?.hardCost || '',
        processingFee: isEditingOrder?.processingFee || '',
        neededDate: isEditingOrder?.neededDate ? moment(isEditingOrder?.neededDate).format('YYYY-MM-DD') : '',
        rush: isEditingOrder?.rush || false
      });
    }
  }, [isEditingOrder?._id]);

  // Accurate roles pick the processors here, so pre-select the county's default ones on a new order. A change of
  // county replaces the selection; otherwise a hand-made one is kept. Other roles don't get the field; the backend
  // assigns the defaults for them.
  useEffect(() => {
    if (!canPreselectProcessors) return;
    const isCountyChanged = !!preselectedCounty.current && preselectedCounty.current !== county;
    preselectedCounty.current = county;
    setFormData(prevData =>
      !isCountyChanged && prevData.assignedProcessors?.length
        ? prevData
        : { ...prevData, assignedProcessors: countyDefaultProcessors }
    );
  }, [countyDefaultProcessors]);

  const isHardCostEmpty = formData.hardCost === '' || formData.hardCost === null || formData.hardCost === undefined;

  const isHardCostInvalid = HARD_COST_REQUIRED_STATUSES.includes(formData.status) && isHardCostEmpty;

  const invoiceLineTotal = useMemo(() => sumInvoiceLineAmounts(isEditingOrder?.feeSourceInvoices), [
    isEditingOrder?.feeSourceInvoices
  ]);

  const collectedFeesDisplay = useMemo(() => {
    if (!isEditingOrder?._id) return null;
    return computeCollectedFees(
      { orderType: 'Lien Search', hardCost: formData.hardCost },
      isEditingOrder.feeSourceInvoices
    );
  }, [isEditingOrder, formData.hardCost]);

  const onHandleClickSubmit = () => {
    if (isPublic || isClient(role)) {
      onSubmit({
        ...formData
      });
    }
    if (formData?.assignedProcessors?.length <= 0) {
      setFormFieldError({ ...formFieldError, isAssignedProcessorsNotValid: true });
      onScrollToErrorPosition('assignedProcessors');
    } else if (!formData?.status) {
      setFormFieldError({ ...formFieldError, isStatusNotValid: true });
      onScrollToErrorPosition('status');
    } else if (isHardCostInvalid) {
      onScrollToErrorPosition('hardCost');
    } else {
      const payload = {
        ...formData,
        assignedProcessors: formData.assignedProcessors.map(a => a?._id)
      };
      onSubmit(payload);
    }
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
      setFormFieldError(InitialFormFieldError);
    }
  };

  const onHandleAssignedProcessors = value => {
    setFormData(prevData => ({ ...prevData, assignedProcessors: value }));
    setFormFieldError(InitialFormFieldError);
  };

  return (
    <Container fluid>
      {!isClient(role) && !isPublic && (
        <AssignedProcessorsMultiSelectModal
          show={showAssignedProcessors}
          onHide={() => setShowAssignedProcesser(false)}
          onConfirm={onHandleAssignedProcessors}
          prevData={formData.assignedProcessors}
          isEditing={isEditingOrder?._id}
        />
      )}
      {!isClient(role) && !isPublic && (
        <Row className="align-items-end">
          <Col md={4} className="p-0">
            <Form.Group className="midFont rounded">
              <Form.Label className="font-weight-bold">
                Status<span className="text-danger">*</span>
              </Form.Label>
              <select
                id="status"
                onChange={e => onFormDataChange('status', e)}
                className={`form-control form-control-sm ${formFieldError.isStatusNotValid && 'input-invalid'}`}
                value={formData.status}
              >
                {lienSearchStatuses.map((order, index) => (
                  <option key={index} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>
                Assigned Processor<span className="text-danger">*</span>
              </Form.Label>
              <div className="position-relative">
                <input
                  id="assignedProcessors"
                  type="text"
                  className={`form-control form-control-sm ${formFieldError.isAssignedProcessorsNotValid &&
                    'input-invalid'}`}
                  onClick={() => setShowAssignedProcesser(true)}
                  placeholder="select"
                  value={
                    formData.assignedProcessors.length > 0
                      ? formData.assignedProcessors.length > 1
                        ? `${formData.assignedProcessors[0]?.name} and ${formData.assignedProcessors.length - 1} others`
                        : formData.assignedProcessors[0]?.name
                      : ''
                  }
                />
                {loadingCountyDefaultProcessors && (
                  <span
                    className="position-absolute d-flex text-muted"
                    style={{ right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  >
                    <Spinner size="sm" animation="border" />
                  </span>
                )}
              </div>
              {formFieldError.isAssignedProcessorsNotValid && (
                <p className="xsmallFont text-rush">This is a required Field</p>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Estimated Turnaround Time</Form.Label>
              <input
                type="date"
                onChange={e => onFormDataChange('estimatedTurnAroundTime', e)}
                className="form-control form-control-sm"
                value={formData.estimatedTurnAroundTime}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      {!isPublic && !isClient(role) && (
        <Row className="mt-4 p-0">
          {!isClient(role) && !isPublic && (
            <>
              <Col md={4}>
                <Form.Group className="midFont">
                  <Form.Label>
                    Hard Cost
                    {HARD_COST_REQUIRED_STATUSES.includes(formData.status) && <span className="text-danger">*</span>}
                  </Form.Label>
                  <input
                    id="hardCost"
                    onChange={e => onFormDataChange('hardCost', e)}
                    type="number"
                    value={formData.hardCost}
                    className={`form-control form-control-sm ${isHardCostInvalid ? 'input-invalid' : ''}`}
                  />
                  {isHardCostInvalid && <p className="xsmallFont text-rush">This is a required Field</p>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="midFont">
                  <Form.Label>Processing Fee</Form.Label>
                  <input
                    onChange={e => onFormDataChange('processingFee', e)}
                    type="number"
                    value={formData.processingFee}
                    className="form-control form-control-sm"
                  />
                </Form.Group>
              </Col>
            </>
          )}
          {!isPublic && !isClient(role) && isEditingOrder?._id && canSeeCollectedFees && (
            <Col md={4} className="midFont text-muted">
              <span className="text-dark d-block">Collected fees</span>
              <span className="text-body">
                {collectedFeesDisplay != null && !Number.isNaN(collectedFeesDisplay)
                  ? `$${Number(collectedFeesDisplay).toFixed(2)}`
                  : 'N/A'}
              </span>
              <div className="xsmallFont text-muted">Total invoice amount: ${invoiceLineTotal.toFixed(2)}</div>
            </Col>
          )}
        </Row>
      )}
      <Col md={6} className="p-0 justify-content-start">
        <div className="form-check p-0 midFont">
          <label className={`mr-1 form-check-label`}>Rush</label>
          <input
            style={{ height: 15, width: 15 }}
            type="checkbox"
            className="form-check-input ml-2"
            checked={formData.rush}
            onChange={() => onFormDataChange('rush', { target: { value: !formData.rush } })}
          />
          <small className="form-text xmidFont text-muted">*Additional charges may apply for a Rush service</small>
        </div>
      </Col>

      <div className="d-flex justify-content-end align-items-end sticky-bottom my-3">
        <Button
          variant="success"
          className="d-inline-flex align-items-center justify-content-center"
          onClick={onHandleClickSubmit}
          disabled={loading}
        >
          {loading && <Spinner size="sm" animation="border" className="mr-1" />}
          {!isEditingOrder?._id ? 'Add' : 'Update'} Lien Search
        </Button>
      </div>
    </Container>
  );
};

export default LienOrderForm;
